/**
 * ProductInstancesList Component
 * Displays and manages product instances for a company
 */

import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Edit2, Trash2, Link2, CheckCircle2, XCircle, AlertCircle, RefreshCw } from 'lucide-react';
import type { ProductInstance } from '@/features/multi-company/types/company.types';
import { getProductDisplayName } from '@/features/multi-company/types/product.types';

interface SyncResult {
  productInstanceId: string;
  status: 'success' | 'partial' | 'failed';
  recordsImported: number;
  recordsUpdated: number;
  recordsFailed: number;
  startedAt: string;
  completedAt: string;
  errors?: string[];
  warnings?: string[];
}

interface ProductInstancesListProps {
  instances: ProductInstance[];
  onUpdate: (instance: ProductInstance) => void;
  onDelete: (instanceId: string) => void;
  onSync?: (instanceId: string) => Promise<SyncResult>;
}

export function ProductInstancesList({
  instances,
  onUpdate,
  onDelete,
  onSync,
}: ProductInstancesListProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [syncConfirmInstance, setSyncConfirmInstance] = useState<ProductInstance | null>(null);
  const [syncingInstanceId, setSyncingInstanceId] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  const handleStartEdit = (instance: ProductInstance) => {
    setEditingId(instance.id);
    setEditingName(instance.instanceName);
  };

  const handleSaveEdit = (instance: ProductInstance) => {
    if (editingName.trim() && editingName !== instance.instanceName) {
      onUpdate({
        ...instance,
        instanceName: editingName.trim(),
      });
    }
    setEditingId(null);
    setEditingName('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleDeleteClick = (instanceId: string) => {
    setDeleteConfirmId(instanceId);
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmId) {
      onDelete(deleteConfirmId);
      setDeleteConfirmId(null);
    }
  };

  const handleSyncClick = (instance: ProductInstance) => {
    setSyncConfirmInstance(instance);
  };

  const handleConfirmSync = async () => {
    if (!syncConfirmInstance || !onSync) {
      setSyncConfirmInstance(null);
      return;
    }

    setSyncingInstanceId(syncConfirmInstance.id);
    setSyncResult(null);
    setSyncError(null);
    setSyncConfirmInstance(null);

    try {
      const result = await onSync(syncConfirmInstance.id);
      setSyncResult(result);
    } catch (error) {
      setSyncError(error instanceof Error ? error.message : 'An error occurred during sync');
    } finally {
      setSyncingInstanceId(null);
    }
  };

  const handleCancelSync = () => {
    setSyncConfirmInstance(null);
  };

  const getStatusIcon = (status: ProductInstance['connectionStatus']) => {
    switch (status) {
      case 'connected':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'not_connected':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: ProductInstance['connectionStatus']) => {
    const variants: Record<string, 'default' | 'destructive' | 'secondary' | 'outline'> = {
      connected: 'default',
      error: 'destructive',
      not_connected: 'secondary',
      not_configured: 'outline',
    };

    return (
      <Badge variant={variants[status] || 'outline'} className="capitalize">
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  if (instances.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-8 text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Products Configured</h3>
          <p className="text-sm text-muted-foreground">
            Click "Add Product" to configure your first product integration
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {instances.map((instance) => {
          const isEditing = editingId === instance.id;
          const isAutoConnected = !!instance.autoConnectedByInstanceId;

          return (
            <Card key={instance.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(instance.connectionStatus)}
                      <h4 className="font-semibold">
                        {getProductDisplayName(instance.productType)}
                      </h4>
                      {isAutoConnected && (
                        <Badge variant="outline" className="text-xs">
                          <Link2 className="h-3 w-3 mr-1" />
                          Auto-connected
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {isEditing ? (
                        <div className="flex items-center gap-2 flex-1">
                          <Input
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            className="h-8"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveEdit(instance);
                              if (e.key === 'Escape') handleCancelEdit();
                            }}
                          />
                          <Button
                            size="sm"
                            onClick={() => handleSaveEdit(instance)}
                            disabled={!editingName.trim()}
                          >
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleCancelEdit}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <>
                          <span className="text-sm text-muted-foreground">
                            Instance: <span className="font-mono">{instance.instanceName}</span>
                          </span>
                        </>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {getStatusBadge(instance.connectionStatus)}
                      {instance.lastSync && (
                        <span className="text-xs text-muted-foreground">
                          Last sync: {new Date(instance.lastSync).toLocaleString()}
                        </span>
                      )}
                    </div>

                    {instance.errorMessage && (
                      <p className="text-xs text-red-500">{instance.errorMessage}</p>
                    )}
                  </div>

                  {!isEditing && (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSyncClick(instance)}
                        title="Sync Data"
                        disabled={syncingInstanceId === instance.id}
                      >
                        <RefreshCw className={`h-4 w-4 ${syncingInstanceId === instance.id ? 'animate-spin' : ''}`} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleStartEdit(instance)}
                        disabled={isAutoConnected}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick(instance.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Product Instance?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the product instance and all its configuration. This action cannot be undone.
              {instances.find(i => i.id === deleteConfirmId)?.autoConnectedByInstanceId && (
                <span className="block mt-2 text-yellow-600">
                  Note: This instance was auto-connected. Deleting the parent product will also remove this instance.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!syncConfirmInstance} onOpenChange={handleCancelSync}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sync Data from {syncConfirmInstance?.instanceName}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to sync data from this instance?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelSync}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSync}>
              Yes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!syncResult || !!syncError} onOpenChange={() => { setSyncResult(null); setSyncError(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {syncError ? 'Sync Failed' : syncResult?.status === 'success' ? 'Sync Completed' : syncResult?.status === 'partial' ? 'Sync Partially Completed' : 'Sync Failed'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                {syncError ? (
                  <p className="text-red-500">{syncError}</p>
                ) : syncResult && (
                  <>
                    <p>
                      <span className="font-medium">Records imported:</span> {syncResult.recordsImported}
                    </p>
                    <p>
                      <span className="font-medium">Records updated:</span> {syncResult.recordsUpdated}
                    </p>
                    {syncResult.recordsFailed > 0 && (
                      <p className="text-red-500">
                        <span className="font-medium">Records failed:</span> {syncResult.recordsFailed}
                      </p>
                    )}
                    {syncResult.errors && syncResult.errors.length > 0 && (
                      <div className="mt-2">
                        <p className="font-medium text-red-500">Errors:</p>
                        <ul className="list-disc list-inside text-sm text-red-500">
                          {syncResult.errors.slice(0, 5).map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                          {syncResult.errors.length > 5 && (
                            <li>...and {syncResult.errors.length - 5} more errors</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => { setSyncResult(null); setSyncError(null); }}>
              Close
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
